import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@commons/header";
import Row from "@components/row";
import Column from "@components/column";
import Table from "@components/table";
import SubscriptionCard from "@commons/cards/subscription";
import TransactionCard from "@commons/cards/transaction";
import Svg from "@components/svg";
import { eye, deletesvg, switchOn, switchOff, edit } from "@assets/icons";
import { useSelector, useDispatch } from "react-redux";
import { Actions, RootState } from "@store/index";
import { DeliveryMan, CodSettlement, Chat } from "@apis/index";
import { useModal } from "@context/ModalContext";
import {
  CheckCircle,
  XCircle,
  DollarSign,
  MessageSquare,
  Wallet,
} from "lucide-react";
import WalletAdjustmentModal from "@components/modals/WalletAdjustmentModal";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import useConfig from "@hooks/useConfig";
import theme from "@utils/theme";
import { toast } from "react-toastify";
import usePermission from "@hooks/usePermission";
import { useQueryState } from "@hooks/useQueryState";

const DeliveryManTable = ({
  type,
}: {
  type: "pending" | "rejected" | "verified";
}) => {
  const { bucketUrl } = useConfig();
  const navigate = useNavigate();
  const { showModal, hideModal } = useModal();
  const [page, setPage] = useQueryState<number>("page", 1);
  const [search, setSearch] = useState("");
  const { hasPermission } = usePermission();
  const { settings: currencySettings } = useGeneralSettings();
  const dispatch = useDispatch();

  const {
    items: deliveryMen = [],
    totalItems,
    totalPages,
    loading,
  } = useSelector((state: RootState) => state.deliveries);
  console.log({ deliveryMen });
  const status =
    type === "pending"
      ? "Pending"
      : type === "rejected"
        ? "Rejected"
        : "Verified";

  const fetchDeliveryMen = useCallback(async () => {
    await DeliveryMan.list({
      state: "deliveries",
      params: { status, page, search },
    });
  }, [status, page, search]);

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setPage(1);
  }, [type, search]);

  useEffect(() => {
    fetchDeliveryMen();
  }, [fetchDeliveryMen]);

  const subscriptionData = [
    {
      count: deliveryMen.length.toString(),
      title: "Total Deliverymen",
      icon: "/pngs/subscription/subscribed-user.png",
      status: "subscribed",
    },
    {
      count: deliveryMen.filter((d) => d.isActive).length.toString(),
      title: "Active Deliverymen",
      icon: "/pngs/subscription/active-user.png",
      status: "active",
    },
    {
      count: deliveryMen.filter((d) => !d.isActive).length.toString(),
      title: "Inactive Deliverymen",
      icon: "/pngs/subscription/expired-user.png",
      status: "expired",
    },
    {
      count: "0",
      title: "Newly Joined",
      icon: "/pngs/subscription/expired-soon.png",
      status: "expiring",
    },
  ];

  const columns = [
    {
      title: "Deliveryman Info",
      key: "deliveryInfo",
      render: (item: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            gap: 12,
          }}
        >
          <div
            style={{
              position: "relative",
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "12px",
                backgroundColor: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontWeight: 600,
                fontSize: "16px",
                border: "1px solid #e2e8f0",
                textTransform: "uppercase",
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 1,
              }}
            >
              {item.name?.charAt(0) || "D"}
            </div>
            {item.avatar && (
              <img
                src={`${bucketUrl}/${item.avatar}`}
                alt={item.name}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "12px",
                  objectFit: "cover",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: 2,
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 600, fontSize: "16px" }}>
              {item.name}
            </span>
            <span style={{ color: "#666", fontSize: "14px" }}>
              {item.phoneNo}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Vehicle / Type",
      key: "vehicleType",
      render: (item: any) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 500, fontSize: "13px" }}>
            {item.vehicleType || "-"}
          </span>
          <span
            style={{
              fontSize: "12px",
              color: item.type === "Salary" ? "#3b82f6" : "#f59e0b",
              fontWeight: 500,
            }}
          >
            {item.type || "-"}
          </span>
        </div>
      ),
    },
    {
      title: "Zone",
      key: "zone",
      render: (item: any) => item.zone?.name || "-",
    },
    {
      title: "Orders (T/O)",
      key: "orders",
      render: (item: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontWeight: 600, fontSize: "14px" }}>
            {item.totalOrders ?? 0}
          </span>
          <span style={{ color: "#94a3b8", fontSize: "12px" }}>/</span>
          <span
            style={{
              fontWeight: 600,
              fontSize: "14px",
              color: (item.ongoingOrders ?? 0) > 0 ? "#f59e0b" : "#94a3b8",
            }}
          >
            {item.ongoingOrders ?? 0}
          </span>
        </div>
      ),
    },
    {
      title: "Wallet",
      key: "walletBalance",
      render: (item: any) => (
        <span
          style={{
            fontWeight: 600,
            fontSize: "13px",
            color:
              (item.walletBalance ?? 0) < 0
                ? "#ef4444"
                : (item.walletBalance ?? 0) > 0
                  ? "#22c55e"
                  : "#64748b",
          }}
        >
          Rs {(item.walletBalance ?? 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "COD Payable to Admin",
      key: "totalReceivable",
      render: (item: any) => (
        <span
          style={{
            fontWeight: 500,
            fontSize: "13px",
            color: (item.totalReceivable ?? 0) > 0 ? "#f59e0b" : "#94a3b8",
          }}
        >
          Rs {(item.totalReceivable ?? 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Online Earnings Owed",
      key: "totalPayable",
      render: (item: any) => (
        <span
          style={{
            fontWeight: 500,
            fontSize: "13px",
            color: (item.totalPayable ?? 0) > 0 ? "#22c55e" : "#94a3b8",
          }}
        >
          Rs {(item.totalPayable ?? 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (item: any) => (
        <Svg
          src={item.isActive ? switchOn : switchOff}
          onClick={async (e) => {
            e.stopPropagation();
            if (!hasPermission("deliveryman", "Edit")) {
              toast.error("You don't have permission to edit deliverymen");
              return;
            }
            await DeliveryMan.update({
              state: "deliveries",
              id: item.id,
              data: { isActive: !item.isActive },
            });
            fetchDeliveryMen();
          }}
        />
      ),
    },
    {
      title: "Joined Date",
      key: "joinedDate",
      render: (item: any) =>
        new Date(item.createdAt).toLocaleDateString() || "-",
    },
  ];

  // Confirmation modal
  const confirmAction = (title: string, onConfirm: () => void) => {
    showModal(
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h3 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "10px" }}>
          {title}
        </h3>
        <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
          <button
            onClick={() => {
              onConfirm();
              hideModal();
            }}
            style={{
              background: "#22c55e",
              color: "white",
              padding: "6px 16px",
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
            }}
          >
            Confirm
          </button>
          <button
            onClick={hideModal}
            style={{
              background: "#ef4444",
              color: "white",
              padding: "6px 16px",
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>,
    );
  };

  // Base actions
  const baseActions = [
    {
      key: "view",
      icon: eye,
      permission: "View List",
      onClick: (item: any) =>
        navigate(
          `/admin/users/delivery-man/delivery-man-lists/view/${item.id}`,
          { state: item },
        ),
    },

    {
      key: "edit",
      icon: edit,
      permission: "Edit",
      onClick: (item: any, e: any) => {
        e?.stopPropagation();
        navigate(`/admin/users/delivery-man/update-delivery-man/${item.id}`, {
          state: item,
        });
      },
    },
    {
      key: "remove",
      icon: deletesvg,
      permission: "Delete",
      onClick: (item: any) => {
        confirmAction("Delete this deliveryman?", async () => {
          console.log("Deleting deliveryman:", item.id);
          // await DeliveryMan.remove({ state: "deliveries", id: item.id });
          fetchDeliveryMen();
        });
      },
    },
    {
      key: "pay",
      icon: <DollarSign size={16} color="#3b82f6" />,
      permission: "View List",
      onClick: (item: any) => {
        showModal(
          <div style={{ padding: "24px", minWidth: "300px" }}>
            <h3 style={{ marginBottom: "16px", fontWeight: 600 }}>
              Mock Delivery Man Payment
            </h3>
            <div style={{ marginBottom: "12px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  marginBottom: "4px",
                }}
              >
                Amount
              </label>
              <input
                id="mock-amount"
                type="number"
                defaultValue="100"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                onClick={hideModal}
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const amount = (
                    document.getElementById("mock-amount") as HTMLInputElement
                  ).value;
                  try {
                    // Note: This mocks the driver API. In reality, the driver token would be used.
                    // This is strictly for user testing from the admin panel.
                    await CodSettlement.createDriverRequest({
                      amount: Number(amount),
                      paymentMethod: "Cash",
                      notes: "Mock payment from Admin Panel",
                    });
                    toast.success(
                      "Settlement request sent (Pending Admin Approval)",
                    );
                    hideModal();
                    fetchDeliveryMen();
                  } catch (err: any) {
                    toast.error(
                      err?.response?.data?.message || "Something went wrong",
                    );
                  }
                }}
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  background: "#3b82f6",
                  color: "white",
                  border: "none",
                }}
              >
                Submit Request
              </button>
            </div>
          </div>,
        );
      },
    },
    {
      key: "wallet",
      icon: <Wallet size={16} color={theme.colors.green500} />,
      permission: "Edit",
      title: "Adjust Wallet",
      onClick: (item: any, e: any) => {
        e.stopPropagation();
        showModal(
          <WalletAdjustmentModal
            user={item}
            onClose={hideModal}
            onSuccess={fetchDeliveryMen}
            currencySettings={currencySettings}
          />,
        );
      },
      style: {
        background: "#f0fdf4",
        border: "1px solid #bbf7d0",
        borderRadius: "8px",
        padding: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s",
      },
    },
  ].filter((action) => hasPermission("deliveryman", action.permission));

  // Pending actions (accept/reject)
  const pendingActions =
    type === "pending"
      ? [
          {
            key: "accept",
            icon: <CheckCircle size={16} color="green" />,
            permission: "Edit",
            onClick: (item: any) => {
              confirmAction("Accept this deliveryman?", async () => {
                await DeliveryMan.update({
                  state: "deliveries",
                  id: item.id,
                  data: { status: "Verified" },
                });
                fetchDeliveryMen();
              });
            },
          },
          {
            key: "reject",
            icon: <XCircle size={16} color="red" />,
            permission: "Edit",
            onClick: (item: any) => {
              confirmAction("Reject this deliveryman?", async () => {
                await DeliveryMan.update({
                  state: "deliveries",
                  id: item.id,
                  data: { status: "Rejected" },
                });
                fetchDeliveryMen();
              });
            },
          },
        ].filter((action) => hasPermission("deliveryman", action.permission))
      : [];

  // Final actions
  const actions =
    type === "pending" ? [...pendingActions, ...baseActions] : baseActions;

  return (
    <div>
      <Header title="Deliverymen" />
      <br />
      <Row>
        {subscriptionData.map((item, i) => (
          <SubscriptionCard key={i} item={item} />
        ))}
      </Row>
      <br />
      {/* <TransactionCard /> */}
      <br />
      <Table
        data={deliveryMen}
        columns={columns}
        actions={actions}
        loading={loading}
        totalPages={totalPages}
        totalItems={totalItems}
        pageControlled={page}
        onPageChange={setPage}
        onSearchChange={setSearch}
        onRowClick={(item: any) =>
          navigate(
            `/admin/users/delivery-man/delivery-man-lists/view/${item.id}`,
            { state: item },
          )
        }
      />
    </div>
  );
};

export default DeliveryManTable;
